import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { HeroScene } from './scene';
import { wants3D } from '../gate-3d';

/**
 * Wraps the static hero picture and, where it is safe, replaces it with the
 * real-time 3D stack.
 *
 * The picture is always rendered first and never removed — it is what the
 * prerender bakes, what crawlers and screen readers get, and what anyone
 * without the 3D sees. The 3D layer mounts over it and the picture fades out
 * only once the first 3D frame has been drawn.
 *
 * Gates (any one keeps the picture):
 *   - `navigator.webdriver` — the prerender and other automation;
 *   - `prefers-reduced-motion: reduce` or `Save-Data`;
 *   - no WebGL2.
 * `?hero3d=1` forces the 3D on and `?hero3d=0` forces it off, for side-by-side
 * comparison.
 *
 * A visitor who gets the 3D never sees the picture: a script in index.html's
 * head applies the same gate before the first paint, hides the picture
 * (<html data-3d="pending">), starts downloading the 3D code with the page and
 * arms the give-up (8 s from navigation). So the scene starts at once here.
 * If it cannot run (no WebGL context, a failed download) or the give-up
 * fires first, data-3d becomes "off" and the picture fades in — and that is
 * final: a 3D that arrives later is thrown away, never swapped in, because a
 * picture that changes into something else is exactly what we are avoiding.
 *
 * `three` is imported only dynamically — it must never enter the main bundle.
 */
const GIVE_UP_MS = 8000;

type DeadlineWindow = Window & { __h3dDeadline?: (ms: number) => void; __h3dArmed?: boolean };

const root = () => document.documentElement;
const showPicture = () => root().setAttribute('data-3d', 'off');
const gaveUp = () => root().getAttribute('data-3d') === 'off';

export function Hero3D({ children }: { children: ReactNode }) {
  const host = useRef<HTMLDivElement | null>(null);
  const stage = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!wants3D()) {
      if (root().hasAttribute('data-3d')) showPicture();
      return;
    }
    // The picture is already showing (the give-up fired before this bundle
    // ran): keep it.
    if (gaveUp()) return;
    const cleanups: Array<() => void> = [];

    // The deadline: armed by the head script on a page load of /; armed here
    // when the hero mounts after a client-side navigation.
    const w = window as DeadlineWindow;
    if (w.__h3dArmed) w.__h3dArmed = false;
    else if (root().hasAttribute('data-3d')) {
      root().setAttribute('data-3d', 'pending');
      if (w.__h3dDeadline) w.__h3dDeadline(GIVE_UP_MS);
      else {
        const t = window.setTimeout(() => root().getAttribute('data-3d') === 'pending' && showPicture(), GIVE_UP_MS);
        cleanups.push(() => window.clearTimeout(t));
      }
    }

    let scene: HeroScene | null = null;
    let cancelled = false;

    const start = async () => {
      const { createHeroScene } = await import('./scene');
      if (cancelled || gaveUp() || !stage.current || !host.current) return;
      scene = createHeroScene(stage.current, () => {
        // Too late: the picture is up. Never swap it.
        if (gaveUp()) {
          scene?.dispose();
          scene = null;
          return;
        }
        root().setAttribute('data-3d', 'on');
        setReady(true);
      });

      // Input only while the hero is on screen: off screen, a scroll or a
      // mouse move must not draw a frame nobody sees.
      let onScreen = true;
      const io = new IntersectionObserver(([e]) => {
        onScreen = e.isIntersecting;
        if (onScreen) onScroll();
      });
      io.observe(host.current);

      // Scroll: the plates part as the hero scrolls away — 0 at the top of
      // the page, 1 once the figure's top has passed the middle of the screen.
      const onScroll = () => {
        if (!onScreen) return;
        const r = host.current!.getBoundingClientRect();
        const p = 1 - (r.top + r.height * 0.15) / (window.innerHeight * 0.85);
        scene?.setExplode(p);
      };
      // Pointer: the stack turns slightly toward the cursor.
      const onPointer = (e: PointerEvent) => {
        if (e.pointerType !== 'mouse' || !onScreen) return;
        const r = host.current!.getBoundingClientRect();
        scene?.setPointer(((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1);
      };
      const onLeave = () => scene?.setPointer(0, 0);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('pointermove', onPointer, { passive: true });
      document.documentElement.addEventListener('pointerleave', onLeave);
      cleanups.push(() => {
        io.disconnect();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('pointermove', onPointer);
        document.documentElement.removeEventListener('pointerleave', onLeave);
      });
      onScroll();
    };

    // At once: the head script has already started the download, and the
    // picture is not shown, so there is nothing for the 3D to wait for.
    start().catch(() => {
      if (!cancelled) showPicture();
    });

    return () => {
      cancelled = true;
      cleanups.forEach((f) => f());
      scene?.dispose();
    };
  }, []);

  return (
    <div ref={host} className="h3d" data-ready={ready || undefined}>
      <div className="h3d-fallback">{children}</div>
      <div ref={stage} className="h3d-stage" aria-hidden />
    </div>
  );
}
